import { findProtectedAtoms } from './protected-atoms';

/**
 * Exact phrase matching at a rule-specified position.
 *
 * Every Chat and navigation cue in the pack is an **exact** phrase at a known
 * position, never a substring anywhere in the query. That is what keeps the rules
 * a protection rather than a guess: `update` at the first semantic position is an
 * action request, but `roadmap update` is a title and must stay on Search.
 *
 * Matching is case-folded and boundary-checked on both sides, and the longest
 * candidate wins so `find me` cannot be pre-empted by `find`.
 */

export type PhraseMatch = {
	/** The lexicon member that matched, in its canonical lower-case spelling. */
	phrase: string;
	/** The remainder of the input with the phrase and its adjoining space removed. */
	rest: string;
};

const isBoundary = (character: string | undefined): boolean =>
	character === undefined || character === ' ';

/**
 * Strips the longest lexicon member that starts the input.
 *
 * Members are compared longest-first rather than in lexicon order, so a pack may
 * list cues in whatever order reads best without changing behaviour.
 */
export const stripLeadingPhrase = (
	input: string,
	phrases: readonly string[],
): PhraseMatch | null => {
	const folded = input.toLowerCase();
	const candidates = [...phrases].sort((left, right) => right.length - left.length);

	for (const phrase of candidates) {
		if (!folded.startsWith(phrase) || !isBoundary(input[phrase.length])) {
			continue;
		}

		const restOffset = phrase.length === input.length ? input.length : phrase.length + 1;

		return { phrase, rest: input.slice(restOffset) };
	}

	return null;
};

/** Strips the longest lexicon member that ends the input. */
export const stripTrailingPhrase = (
	input: string,
	phrases: readonly string[],
): PhraseMatch | null => {
	const folded = input.toLowerCase();
	const candidates = [...phrases].sort((left, right) => right.length - left.length);

	for (const phrase of candidates) {
		if (!folded.endsWith(phrase)) {
			continue;
		}

		const start = input.length - phrase.length;

		if (!isBoundary(input[start - 1])) {
			continue;
		}

		return { phrase, rest: input.slice(0, Math.max(0, start - 1)) };
	}

	return null;
};

/**
 * Splits a span on the first lexicon separator that sits outside every protected
 * range.
 *
 * `compare "a and b" with c` must split on `with`, not on the `and` inside the
 * quoted operand, which is why the offsets of protected atoms are consulted rather
 * than the text alone.
 */
export const splitOnSeparator = (
	span: string,
	separators: readonly string[],
): { left: string; right: string; separator: string } | null => {
	const atoms = findProtectedAtoms(span);
	const isProtected = (start: number, end: number): boolean =>
		atoms.some((atom) => start < atom.end && end > atom.start);
	const folded = span.toLowerCase();
	const candidates = [...separators].sort((left, right) => right.length - left.length);

	for (let index = 0; index < span.length; index += 1) {
		if (index !== 0 && span[index - 1] !== ' ') {
			continue;
		}

		const separator = candidates.find(
			(candidate) =>
				folded.startsWith(candidate, index) && isBoundary(span[index + candidate.length]),
		);

		if (!separator || isProtected(index, index + separator.length)) {
			continue;
		}

		return {
			left: span.slice(0, Math.max(0, index - 1)),
			right: span.slice(Math.min(span.length, index + separator.length + 1)),
			separator,
		};
	}

	return null;
};
